// 2. External libraries
import type { useAuth } from '@clerk/clerk-react';

// The subset of Clerk's useAuth return the token getter depends on.
type ClerkAuthSlice = Pick<ReturnType<typeof useAuth>, 'getToken' | 'isLoaded' | 'isSignedIn'>;

/**
 * Builds the async token getter the API mutator awaits on every request.
 *
 * Returns null (so the mutator omits the Authorization header) before Clerk
 * loads, while signed out, when the token resolves empty, or when resolution
 * fails, so the request proceeds unauthenticated rather than blocking or sending
 * `Bearer ` with no value.
 *
 * Both the render-time registration in `main.tsx` (which covers route loaders
 * that run before any component mounts) and the effect-time registration in
 * `AuthTokenBridge` (which keeps the getter current as the session changes)
 * build the getter through this single factory, so their behavior cannot drift.
 */
export function createClerkTokenGetter({
  getToken,
  isLoaded,
  isSignedIn,
}: ClerkAuthSlice): () => Promise<string | null> {
  return async () => {
    // Before Clerk loads or while signed out, omit the header instead of
    // sending a stale or empty token.
    if (!isLoaded || !isSignedIn) {
      return null;
    }

    try {
      const token = await getToken();
      // Treat an empty token as absent so the mutator omits the header.
      return token ? token : null;
    } catch {
      return null;
    }
  };
}
